import React, { useCallback, useEffect, useState } from 'react';
import { IBackgroundImages, IPopularSearch } from 'api/home/types';
import { IRegionShort } from 'api/region/types';
import { sendMessageToAiAssistant } from 'util/sendMessageToAiAssistant';
import { SearchPopup } from 'components/home/SearchPopup';
import { NextLink } from 'components/blocks/NextLink';
import { Routes } from 'routes';
import { useOTSTranslation } from 'i18n/useOTSTranslation';
import { useAnalytics } from 'util/analytics';
import { setFocusToSearchInput } from 'util/setFocusToInputByQuery';
import { ReactComponent as SvgCrystal } from 'public/assets/ai-assistant-logo-homepage.svg';
import { ReactComponent as SearchIcon } from 'public/assets/icons/search.svg';
import classNames from 'classnames';
import styles from './style.module.scss';

type HeroMode = 'skigpt' | 'classic';

export const AiHomeHero = ({
  backgroundImage,
  suggestions = [],
  popularSearch,
  searchQuery,
  clearSearchQuery,
}: {
  backgroundImage: IBackgroundImages;
  suggestions?: string[];
  popularSearch: IPopularSearch;
  searchQuery?: string | string[] | undefined;
  clearSearchQuery?: () => void;
}) => {
  const { t } = useOTSTranslation('common');
  const a = useAnalytics();

  const [mode, setMode] = useState<HeroMode>('skigpt');

  const [inputValue, setInputValue] = useState('');

  const handleSend = () => {
    if (inputValue.trim()) {
      sendMessageToAiAssistant(inputValue);
      setInputValue('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSuggestionClick = (text: string) => {
    sendMessageToAiAssistant(text);
  };

  const [isSearchOpen, setSearchOpen] = useState<boolean>(false);

  const toggleSearch = useCallback(() => {
    new Promise<void>((resolve) => {
      if (clearSearchQuery) {
        clearSearchQuery();
      }
      setSearchOpen(!isSearchOpen);
      if (!isSearchOpen) {
        a.searchBox();
      }
      resolve();
    }).then(() => {
      setFocusToSearchInput();
    });
  }, [isSearchOpen, clearSearchQuery, a]);

  useEffect(() => {
    setSearchOpen(!!searchQuery);
  }, [searchQuery]);

  return (
    <header
      className={styles.hero}
    >
      <img
        src={backgroundImage.image}
        alt=""
        className={styles.heroBgImage}
      />
      {isSearchOpen && (
        <SearchPopup
          closePopup={toggleSearch}
          query={searchQuery}
        />
      )}

      <div className={styles.container}>
        <div className={styles.tabSwitcher}>
          <button
            type="button"
            className={classNames(
              mode === 'skigpt' ? styles.tabActive : styles.tabInactive,
              styles.tabLeft,
            )}
            onClick={() => setMode('skigpt')}
          >
            <SvgCrystal
              className={classNames(
                styles.tabCrystalIcon,
                mode !== 'skigpt' && styles.tabCrystalIconInactive,
              )}
            />
            SkiGPT Mode
          </button>
          <button
            type="button"
            className={classNames(
              mode === 'classic' ? styles.tabActive : styles.tabInactive,
              styles.tabRight,
            )}
            onClick={() => setMode('classic')}
          >
            Classic Search
          </button>
        </div>

        <div className={styles.contentCard}>
          <div className={styles.titleRow}>
            <img
              className={styles.skiGptLogo}
              src="/assets/SkiGPT-logo.svg"
              alt="SkiGPT"
            />
            <h2 className={styles.title}>Your Personal AI Ski Concierge</h2>
          </div>

          {mode === 'skigpt' && (
            <>
              <div className={styles.searchBox}>
                <textarea
                  className={styles.searchInput}
                  placeholder="Planning your next trip to the the mountains? Ask SKIGPT about fresh powder, weather changes, or help planning your trip."
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  maxLength={450}
                />
                <button type="button" className={styles.sendButton} onClick={handleSend}>
                  <img src="/assets/icons/send-icon.svg" alt="Send message to SkiGPT" />
                </button>
              </div>

              {suggestions.length > 0 && (
                <div className={styles.suggestions}>
                  {suggestions.map((text) => (
                    <button
                      key={text}
                      type="button"
                      className={styles.pill}
                      onClick={() => handleSuggestionClick(text)}
                    >
                      {text}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {mode === 'classic' && (
            <div className={styles.classicPanel}>
              <div className={styles.classicSearchRow}>
                <input
                  type="text"
                  className={styles.classicSearchInput}
                  placeholder={t('homePage.searchBarHelpText')}
                  onClick={toggleSearch}
                  onFocus={(e) => e.target.blur()}
                  readOnly
                />
                <button
                  type="button"
                  className={styles.classicSearchButton}
                  onClick={toggleSearch}
                >
                  <span className={styles.desktopButtonText}>{t('common.searchButton')}</span>
                  <SearchIcon className={styles.mobileSearchIcon} />
                </button>
              </div>

              {popularSearch && popularSearch.results && (
                <div className={styles.popularGrid}>
                  {popularSearch.results.slice(0, 5).map((item) => {
                    if (typeof item === 'string') {
                      return (
                        <button
                          key={item}
                          type="button"
                          className={styles.popularCell}
                          onClick={toggleSearch}
                        >
                          {item}
                        </button>
                      );
                    }
                    const region = item as IRegionShort;
                    return (
                      <NextLink
                        key={region.uuid}
                        href={Routes.regionSnowReport(region)}
                        className={styles.popularCell}
                      >
                        {region.title}
                      </NextLink>
                    );
                  })}
                  <NextLink
                    href={Routes.nearbyResorts()}
                    className={styles.popularCell}
                  >
                    {t('homePage.nearbyResorts')}
                  </NextLink>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
